<template>
  <div>
    <!-- <v-hover v-if="canCreate" v-slot="{ hover }">
      <v-btn
        :class="{ 'create-button': true, hovered: hover }"
        color="black"
        fab
        dark
        fixed
        bottom
        right
        @click="create"
      >
        <v-icon>mdi-plus</v-icon>
      </v-btn>
    </v-hover> -->

    <v-container fluid class="pt-0 mx-0">
      <core-table
        ref="coreTable"
        v-bind="$attrs"
        style="background: transparent"
        :headers="headers"
        :items="_.isEmpty(currentSearch)
          ? itemsInStore
          : _.filter(itemsInStore, i =>
            _.includes(_.toLower(JSON.stringify(i)), _.toLower(currentSearch)))"
        :item-key="itemKey"
        :loading="isLoading"
        :loading-text="loadingText"
        :menu-items="menuItems"
        :sort-key.sync="currentSortKey"
        :sort-desc.sync="currentSortDesc"
        :page.sync="currentPage"
        :limit.sync="currentLimit"
        :entity.sync="currentEntity"
        :selected.sync="currentSelected"
        :hovered.sync="currentHovered"
        :search.sync="currentSearch"
        :total="totalInStore"
        :hide-search="hideSearch"
        @click-row="clickRow"
        @click-expand="clickExpand"
        @menu-click="menuClick"
        @refresh="refresh"
      >
        <div v-for="field in headers" :key="field.value.toString()">
          {{ field.value }}
        </div>

        <template
          v-for="field in headers"
          :slot="`item.${slotFieldName(field)}`"
          slot-scope="props"
        >
          <slot :name="`item.${slotFieldName(field)}`" v-bind="props" />
        </template>

        <template slot="expanded-item" slot-scope="props">
          <slot name="expanded-item" v-bind="props" />
        </template>
      </core-table>
    </v-container>


    <v-dialog
      v-model="removeDialog"
      max-width="300px"
      persistent
    >
      <v-card>
        <v-card-title class="headline">
          {{ $tc('dialogs.remove.title', _.size(removeEntities)) }}
        </v-card-title>

        <v-card-text>
          {{ $tc('dialogs.remove.message', _.size(removeEntities)) }}
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            color="error darken-1"
            text
            @click="removeDialog = false"
          >
            {{ $t('common.no') }}
          </v-btn>

          <v-btn
            color="success darken-1"
            text
            @click="removeConfirm"
          >
            {{ $t('common.yes') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import ServiceMixin from '../../mixins/service';
import TableMixin from '../../mixins/table';
import AuthMixin from '../../mixins/auth';
import { LEVEL } from '../../utils/log';

/**
 * Events:
 *
 *    refresh Called when a refresh of data might be needed.
 *
 *    menu-click Called when a radial menu item is clicked on.
 *      {object} menu Menu item clicked.
 *      {object} entity Document the menu was opened on.
 */
export default {
  mixins: [
    ServiceMixin,
    TableMixin,
    AuthMixin,
  ],

  props: {
    // Don't auto-create a new tab, just emit a before-create event
    beforeCreate: Boolean,

    // Don't auto-remove, just emit a before-remove event
    beforeRemove: Boolean,

    /** RegExp aggregates (joins) to execute for a search
     * This is a very complicated process to go in Mongo,
     * so I try to simplify it a bit (hopefully I didn't make it
     * harder). Please follow the instructions from the Mongo
     * documentation here:
     * https://docs.mongodb.com/manual/reference/operator/aggregation-pipeline/
     *
        ex:
        [
          {
            from: 'users',
            localField: 'createdById',
            foreignField: '_id',
            as: 'createdBy',
            searchFields: ['displayName', 'email']
          }
        ]
     */
    searchAggregate: Array,

    /** Table filters
     *    label: string
     *    icon: string
     *    value: string
     */
    filters: { type: Array, default: () => [] },

    // Field to apply filter to
    filterField: String,

    /** Format in which filters are displayed
     *    buttons   | Buttons group
     *    select    | Select input
     */
    filterFormat: {
      type: String,
      default: 'buttons',
      validator: (value) => ['buttons', 'select'].includes(value),
    },

    // Label for filters select format component
    filterLabel: String,

    filterClearable: Boolean,
  },

  data() {
    return {
      removeDialog: false,
      removeEntities: [],
    };
  },

  computed: {
    /**
     * Returns if still loading items or store state.
     * If no service name property is provided, the loading property will
     * be used instead.
     *
     * @returns {boolean}
     */
    isLoading() {
      return _.get(this.storeState, 'isFindPending', this.loading);
    },

    /**
     * Generates a local/store compatible query object.
     *
     * @returns {object}
     */
    mongooseLocalQuery() {
      return this.generateQuery({
        local: true,
        limit: this.currentLimit,
        page: this.currentPage,
        sortKey: this.currentSortKey,
        sortDesc: this.currentSortDesc,
        search: this.delayedSearch,
        searchFields: _.map(this.headers, 'value'),
        searchAggregate: this.searchAggregate,
        filter: { field: this.filterField, value: this.currentFilter },
        query: this.currentLocalQuery || this.currentQuery,
      });
    },

    /**
     * Generates a remote store mongoose compatible query object.
     *
     * @returns {object}
     */
    mongooseRemoteQuery() {
      return this.generateQuery({
        local: false,
        limit: this.currentLimit,
        page: this.currentPage,
        sortKey: this.currentSortKey,
        sortDesc: this.currentSortDesc,
        search: this.delayedSearch,
        searchFields: _.map(this.headers, 'value'),
        searchAggregate: this.searchAggregate,
        filter: { field: this.filterField, value: this.currentFilter },
        query: this.currentRemoteQuery || this.currentQuery,
      });
    },

    /**
     * Returns the total number of documents.
     * If a service name property is provided, it returns
     * the total number of documents in the local store
     * else it returns the total property value.
     *
     * @returns {number}
     */
    totalInStore() {
      return this.currentTotal;
    },

    /**
     * Returns the documents.
     * If a service name property is provided, it returns
     * the filtered documents that are in the local store
     * else it returns the items property value.
     *
     * @returns {[object]}
     */
    itemsInStore() {
      return this.find({ query: this.mongooseLocalQuery }).data;
    },
  },

  mounted() {
    this.refresh();
  },

  activated() {
    this.refresh();
  },

  beforeDestroy() {
    this.removeEntities = [];
  },

  methods: {
    ...mapActions('app', ['pushSnack']),

    /**
     * Checks delete rights, currentSelected count property
     * to see if deletion is allowed.
     */
    async canRemove() {
      return (await this.canDelete) && this.currentSelected.length > 0;
    },

    /**
     * Checks read rights and currentSelected count property
     * to see if viewing the entity is allowed.
     */
    async canView() {
      return this.canRead;
    },

    /**
     * Query the local store.
     *
     * @param {object} params
     */
    find(params) {
      // fix v-data-table issue
      if (_.get(params, 'query.$limit') === -1) {
        delete params.query.$limit;
      }
      const r = this.store
        ? this.storeGetter('find')(params)
        : {
          data: this.items,
          total: _.size(this.items),
        };
      this.currentTotal = r.total;
      this.adjustPage();
      return r;
    },

    /**
     * Query and fetch documents from remote.
     *
     * @param {object} params
     */
    fetch(params) {
      // fix v-data-table issue
      if (_.get(params, 'query.$limit') === -1) {
        delete params.query.$limit;
      }
      const r = this.store
        ? this.storeDispatch('find', params)
        : {
          data: this.items,
          total: _.size(this.items),
        };
      this.currentTotal = r.total;
      this.adjustPage();
      return r;
    },

    /**
     * Make sure current page is not out of bounds.
     */
    adjustPage() {
      let page = this.currentPage;
      while (page > 0 && (page - 1) * this.currentLimit >= this.currentTotal) {
        page--;
      }
      this.currentPage = page;
    },

    /**
     * Fetch and adjust page and update currentTotal.
     */
    async refresh() {
      if (this.model) {
        await this.fetch({ query: this.mongooseRemoteQuery });
      }
      this.$emit('refresh');
    },

    /**
     * Re-emits menu-click event.
     */
    menuClick(menu, entity) {
      this.$emit('menu-click', menu, entity);
    },

    /**
     * Re-emits click-row event.
     */
    clickRow(entity) {
      this.$emit('click-row', entity);
    },

    /**
     * Re-emits click-expand event.
     */
    clickExpand(entity) {
      this.$emit('click-expand', entity);
    },

    /**
     * Create a new tab with a new service model instance.
     */
    create() {
      if (this.beforeCreate) {
        this.$emit('before-create');
      } else {
        this.$emit('create');
      }
    },

    /**
     * Clear selected entities.
     */
    clearSelection() {
      this.currentSelected = [];
    },

    /**
     * Remove the document (local and remote).
     *
     * @param {string} _id ID of the document to remove.
     */
    removeEntity(_id) {
      return this.store
        ? this.storeDispatch('remove', _id).then(() => this.$emit('tab-remove', -1, _id))
        : Promise.resolve(() => this.$emit('remove', -1, _id));
    },

    /**
     * Ask if you want to remove the document(s).
     */
    async remove() {
      if (this.beforeRemove) {
        this.$emit('before-remove', this.currentSelected);
      } else {
        this.removeDialog = true;
        this.removeEntities = _.clone(this.currentSelected);
      }
    },

    /**
     * Removes all currentSelected entities (local and remote).
     */
    async removeConfirm() {
      this.removeDialog = false;
      const promises = _.map(this.removeEntities, (entity) => this.removeEntity(entity._id));
      Promise.all(promises)
        .then(() => {
          this.pushSnack({
            snack: { message: this.$t('common.success.delete') },
            level: LEVEL.SUCCESS,
          });
          this.clearSelection();
          this.refresh();
        })
        .catch((e) => {
          this.pushSnack({
            snack: { message: `${this.$t('common.error.delete')}\n${e.message}` },
            level: LEVEL.ERROR,
          });
          console.error(e);
        });
    },

    slotFieldName(field) {
      let fn = _.get(field, 'value', '').toString();
      if (fn.indexOf('.') !== -1) {
        fn = _.camelCase(fn.replace('.', '_'));
      }
      return fn;
    },
  },
};
</script>

<style scoped>
.create-button {
  /* position: absolute;
  right: 1em;
  top: -1.5em; */
  opacity: .65;
  z-index: 8;
}
.hovered {
  opacity: 1;
}
</style>
